import { ILayerState } from "../config";
import { getMap } from "../GeoLiteMap";
import { IIdentifyOrder, IIdentifyTolerance } from "../IUserConfig";
import { isMobile } from "../Toc/helpers";
import { Layer, Tile as TileLayer, Image as ImageLayer } from "ol/layer";
import { decodeFeatureFields } from "../utils";
import { Vector as VectorLayer } from "ol/layer";
import BaseLayer from "ol/layer/Base";
import { Feature } from "ol";
import VectorSource from "ol/source/Vector";
import { Pixel } from "ol/pixel";

export type IdentifyResultsArray = [string, IdentifyResult[]][];

export interface IdentifyResult {
    mapLayerId: string;
    layerId: number;
    layerName: string;
    feature: Feature;
}

export const getIdentifyLayers = (config, layerStates: ILayerState[]) =>
    getMap()
        .getLayers()
        .getArray()
        .filter((layer: Layer) => {
            const existsInConfig = config.layers.find((layerConfig) => {
                const isLayerVisible = layerStates.find((state) => {
                    if (state.type === "LayerGroup" && state.sublayers.length) {
                        return state.sublayers.find((subLayer) => subLayer.layerId === layerConfig.id && state.visible);
                    }
                    return state.layerId === layerConfig.id && state.visible;
                });
                return isLayerVisible && layer.get("id") === layerConfig.id;
            });
            // return layer.type !== "graphics" && existsInConfig;
            return existsInConfig;
        });

export const getVisibleSubLayers = (layersConfig, mapLayer, excludes): string => {
    let allowedLayers: number[] = [];
    let layerIds = [];

    if (mapLayer instanceof TileLayer) {
        return "all";
    }

    if (mapLayer instanceof ImageLayer) {
        const visibleSubLayers = mapLayer
            .getSource()
            .getParams()
            .LAYERS.replace("show:", "")
            .split(",")
            .map((nr) => Number(nr));

        if (mapLayer.get("layers")) {
            allowedLayers = getSubLayers(mapLayer.get("layers"), allowedLayers, visibleSubLayers);
        }

        const layerConfig = layersConfig.find((element) => element.id === mapLayer.get("id"));
        if (layerConfig && layerConfig.identify && "rest" in layerConfig.identify) {
            allowedLayers = Object.keys(layerConfig.identify)
                .filter((key) => key !== "rest")
                .map((key) => parseInt(key));
        }

        if (!layerConfig || (layerConfig && layerConfig.identify === null)) {
            return;
        }

        layerIds = allowedLayers;

        if (Array.isArray(excludes)) {
            layerIds = layerIds.filter((x: number) => !excludes.includes(x));
        }
        if (layerIds.length === 0 && allowedLayers.length > 0) {
            return;
        }

        return "visible:" + layerIds.filter((id) => visibleSubLayers.includes(id)).join(",");
    }
};

const getSubLayers = (layers, layerIds: number[], visibleSubLayers: number[]) => {
    let allowedLayers = layerIds;
    layers.map((layer) => {
        layer.sublayers?.length
            ? (allowedLayers = getSubLayers(layer.sublayers, allowedLayers, visibleSubLayers))
            : visibleSubLayers.includes(layer.id) && allowedLayers.push(layer.id);
    });
    return allowedLayers;
};

export const getExcludeLayers = (layer, layers) => {
    const curLayer = layers.filter((element) => element.id === layer.id);
    if (curLayer.length > 0) {
        const firstLayer = curLayer[0];

        if (typeof firstLayer.identify === "undefined") {
            return "undefined";
        }
        if (!firstLayer.identify) {
            return false;
        }

        const excludeArray: number[] = [];
        for (const exclude in firstLayer.identify) {
            if (!firstLayer.identify[exclude]) {
                excludeArray.push(Number(exclude));
            }
        }
        return excludeArray;
    }
    return "undefined";
};

export const getIdentifyTolerance = (identifyTolerance?: IIdentifyTolerance): number => {
    const defaultMobileIdentifyTolerance = 10;
    const defaultDesktopIdentifyTolerance = 5;
    const mobileTolerance = identifyTolerance ? identifyTolerance.mobile : defaultMobileIdentifyTolerance;
    const desktopTolerance = identifyTolerance ? identifyTolerance.desktop : defaultDesktopIdentifyTolerance;

    return isMobile() ? mobileTolerance : desktopTolerance;
};

export const isFeatureValid = (attributes, exclude: string[], attribute: string, isLinkField: boolean): boolean =>
    !!{}.hasOwnProperty.call(attributes, attribute) &&
    attributeNotIncludedIn(exclude, attribute) &&
    attribute !== "geometry" &&
    attributes[attribute] &&
    // (isLinkField || attributes[attribute] !== "Null");
    attributes[attribute] !== "Null";

export const attributeNotIncludedIn = (exclude: string[], attribute: string): boolean =>
    !(
        exclude.includes(attribute) ||
        exclude.includes(attribute.toUpperCase()) ||
        exclude.includes(attribute.toLowerCase())
    );

export const identifyVectorLayers = (mapLayers: BaseLayer[], pixel: Pixel): IdentifyResultsArray => {
    const identifiedResults = mapLayers
        .map(
            (layer: BaseLayer) =>
                layer instanceof VectorLayer && layer.get("identify") && identifyFeaturesAtPixel(layer, pixel)
        )
        .filter((res) => res);
    if (!identifiedResults) {
        return;
    }
    const resultArray: IdentifyResultsArray = [];
    identifiedResults.flat().map((res) => !resultArray.some((ra) => ra[0] === res[0]) && resultArray.push(res));

    return resultArray;
};

export const identifyFeaturesAtPixel = (mapLayer: VectorLayer<VectorSource>, pixel: Pixel): IdentifyResultsArray => {
    const layerId = mapLayer.get("id");
    const pixelFeatures = getMap().getFeaturesAtPixel(pixel, {
        layerFilter: (layer) => layer.get("id") === layerId
    }) as Feature[];

    if (!pixelFeatures?.length) {
        return;
    }
    const resultArray: IdentifyResultsArray = [];
    const results = pixelFeatures[0].get("features")
        ? identifyClusterFeatures(pixelFeatures[0].get("features"), resultArray)
        : identifyFeatures(mapLayer, pixel);

    return results?.length && results;
};

export const identifyClusterFeatures = (
    features: Feature[],
    resultArray?: IdentifyResultsArray
): IdentifyResultsArray => {
    const results: IdentifyResultsArray = resultArray || [];
    features.map((f) => {
        const layerId = f.get("layerData").id;
        const fieldInfo = getMap()
            .getLayers()
            .getArray()
            .find((ml) => ml.get("id") === layerId)
            ?.get("fieldInfo");
        pushResult(f.get("layerData").identify, layerId, f, fieldInfo, results);
    });
    return results.length && results;
};

export const identifyFeatures = (mapLayer: VectorLayer<VectorSource>, pixel: Pixel): IdentifyResultsArray => {
    const layerId = mapLayer.get("id");
    const results: IdentifyResultsArray = [];
    getMap().forEachFeatureAtPixel(
        pixel,
        (featureLike, layer) => {
            const clusterFeature = featureLike.get("features");
            const identFeature = clusterFeature ? clusterFeature[0] : featureLike;
            pushResult(layer.get("identify"), layerId, identFeature, layer.get("fieldInfo"), results);
        },
        {
            layerFilter: (layer) => layer.get("id") === layerId
        }
    );
    return results.length && results;
};

const pushResult = (identifyConfig, layerId, identFeature, fieldInfo, results) => {
    const layerName = identifyConfig[layerId]?.layerName;
    if (!layerName) {
        return;
    }

    const feature = decodeFeatureFields(identFeature as Feature, fieldInfo);

    let layerElement = results.find((resEl) => resEl[0] === layerName);
    if (!layerElement) {
        layerElement = [layerName, []];
        results.push(layerElement);
    }

    layerElement[1].push({
        mapLayerId: layerId,
        layerId,
        layerName,
        feature
    } as IdentifyResult);
};

export const sortIdentifyResults = (results: IdentifyResultsArray, order: IIdentifyOrder[]): IdentifyResultsArray => {
    const sorted = results.sort((a, b) => {
        const serviceIndexA = indexOfService(order, a[1][0].mapLayerId);
        const serviceIndexB = indexOfService(order, b[1][0].mapLayerId);

        return serviceIndexA === -1
            ? 1
            : serviceIndexB === -1
            ? -1
            : serviceIndexA < serviceIndexB
            ? -1
            : serviceIndexA > serviceIndexB
            ? 1
            : serviceIndexA === serviceIndexB
            ? sortByLayerIds(order, a, b)
            : 0;
    });

    return sorted;
};

const sortByLayerIds = (order: IIdentifyOrder[], a, b): number => {
    const layerOrder = order.find((ord) => ord.serviceId.toLowerCase() === a[1][0].mapLayerId.toLowerCase())?.layerIds;
    if (!layerOrder || !layerOrder.length) {
        return 0;
    }

    const layerIndexA = layerOrder.indexOf(a[1][0].layerId);
    const layerIndexB = layerOrder.indexOf(b[1][0].layerId);

    return layerIndexA === -1
        ? 1
        : layerIndexB === -1
        ? -1
        : layerIndexA < layerIndexB
        ? -1
        : layerIndexA > layerIndexB
        ? 1
        : 0;
};

const indexOfService = (order: IIdentifyOrder[], value: string) => {
    for (let i = 0; i < order.length; i++) {
        if (order[i].serviceId.toLowerCase() === value.toLowerCase()) {
            return i;
        }
    }
    return -1;
};

export const replaceWithValue = (feature: Feature, text?: string): string => {
    if (!text) {
        return;
    }
    const replacedWithValues = text.replace(/[^{}]+(?=})/g, (matched: string) => feature.get(matched) || matched);

    return replacedWithValues
        .replace(/{undefined}/g, "")
        .replace(/{null}/g, "")
        .replace(/[{}]/g, "");
};
